import { cva, type VariantProps } from "./style.utils";

// -----------------------------------------------------------------------------

export type StepsRootVariants = VariantProps<typeof stepsRootVariants>;

export const stepsRootVariants = cva({
  base: "",
});

// -----------------------------------------------------------------------------

export type StepsListVariants = VariantProps<typeof stepsListVariants>;

export const stepsListVariants = cva({
  base: [
    // layout
    "flex w-fit flex-col flex-nowrap",
    // md layout
    "md:mx-auto md:grid md:auto-cols-fr md:grid-flow-col",
  ],
});

// -----------------------------------------------------------------------------

export type StepsListItemVariants = VariantProps<typeof stepsListItemVariants>;

export const stepsListItemVariants = cva({
  base: [
    // layout
    "flex items-center gap-10px",
    // layout spacing
    "py-5px first:-mt-5px last:-mb-5px",

    // lg layout
    "md:flex-col md:gap-5px",
    // lg layout spacing reset
    "md:py-0 md:first:mt-0 md:last:mb-0",
    // lg layout spacing
    "md:px-10px md:first:-ml-10px md:last:-mr-10px",

    // positioning
    "relative",
  ],
});

// -----------------------------------------------------------------------------

export type StepsTrackVariants = VariantProps<typeof stepsTrackVariants>;

export const stepsTrackVariants = cva({
  base: [
    // layout
    "flex h-full w-1px flex-row",
    // layout translate
    "-translate-x-1/2",
    // positioning
    "absolute left-12px top-0",

    // lg layout
    "md:h-1px md:w-full md:flex-row",
    // lg layout translate reset
    "md:translate-x-0",
    // lg layout translate
    "md:-translate-y-1/2",
    // lg positioning
    "md:left-0 md:top-12px",
  ],
  variants: {
    variant: {
      success: "",
      current: "",
      pending: "",
    },
    edge: {
      start: "items-end md:items-stretch md:justify-end",
      center: "",
      end: "",
    },
  },
  compoundVariants: [
    {
      edge: ["start", "end"],
      className: [
        // layout
        "*:h-1/2 *:w-full md:*:h-full md:*:w-1/2",
        // background
        "bg-transparent",
      ],
    },

    {
      edge: "start",
      variant: "success",
      className: "*:bg-white",
    },
    {
      edge: "start",
      variant: "current",
      className: "*:bg-white/50",
    },
    {
      edge: "start",
      variant: "pending",
      className: "*:bg-white/50",
    },
    {
      edge: "center",
      variant: "success",
      className: "bg-white",
    },
    {
      edge: "center",
      variant: "current",
      className: [
        // layout
        "*:h-1/2 *:w-full md:*:h-full md:*:w-1/2",
        // background
        "bg-white/50 *:bg-white",
      ],
    },
    {
      edge: "center",
      variant: "pending",
      className: "bg-white/50",
    },
    {
      edge: "end",
      variant: "success",
      className: "*:bg-white",
    },
    {
      edge: "end",
      variant: "current",
      className: "*:bg-white",
    },
    {
      edge: "end",
      variant: "pending",
      className: "*:bg-white/50",
    },
  ],
  defaultVariants: {
    variant: "pending",
    edge: "center",
  },
});

// -----------------------------------------------------------------------------

export type StepsIndicatorVariants = VariantProps<
  typeof stepsIndicatorVariants
>;

export const stepsIndicatorVariants = cva({
  base: [
    // layout
    "size-24px shrink-0",
    // positioning above track
    "z-[1]",
    // radii
    "rounded-full",
    // svg secondary fill/stroke
    "[--color-steps-indicator:#13528C]", // banner-color
  ],
  variants: {
    variant: {
      success: "text-white",
      current: "text-white",
      pending: "text-white/50",
    },
  },
  defaultVariants: {
    variant: "pending",
  },
});

// -----------------------------------------------------------------------------

export type StepsContentVariants = VariantProps<typeof stepsContentVariants>;

export const stepsContentVariants = cva({
  base: "text-left text-xs font-semibold leading-tight md:text-center",
  variants: {
    variant: {
      success: "text-white",
      current: "text-white",
      pending: "text-white/75",
    },
  },
  defaultVariants: {
    variant: "pending",
  },
});
